// @ts-strict-ignore
import React, { useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { DragDropProvider } from '@dnd-kit/react';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import type { DragEndEvent } from 'common/components/draggable-list/types';
import { findKeyByValue } from 'common/util/typescript';
import { useTranslation } from 'common/i18n/hooks';
import { handleOnBeforeDrag } from 'common/components/draggable-list/utils';
import type { ReportState } from '../report-bookmark-manager';
import { handleFilterAcrossMetricEvents, handleGlobalFilerToMetricEvent, handleMetricEventFiltersRearrange, handleMetricEventsRearrange } from './metric-events';
import { handleGlobalFiltersRearrange } from './filters';
import { handleMetricsRearrange } from './metrics';
import { BookmarkDragOperationMetadata } from './types';

/**
 * DragDropProvider for handling drag and drop operations related to bookmarks in reports.
 * This takes care of interactions like dragging a filter onto a metric event, rearranging different sections throughout the report, etc...
 */
export function BookmarkDragDropProvider(props: {
  report: ReportState;
  children: React.ReactNode;
}) {
  const i18n = useTranslation();
  const {
    updateParams
  } = props.report;
  const params = cloneDeep(props.report.params);
  const onDragEnd = useCallback((event: DragEndEvent<BookmarkDragOperationMetadata>) => {
    var _event$nativeEvent, _event$operation$targ, _event$operation$sour, _targetData$behaviorI, _sourceData$behaviorI;
    const isCloning = Boolean((_event$nativeEvent = event.nativeEvent as MouseEvent) === null || _event$nativeEvent === void 0 ? void 0 : _event$nativeEvent.altKey);
    const targetData = ((_event$operation$targ = event.operation.target) === null || _event$operation$targ === void 0 ? void 0 : _event$operation$targ.data) as BookmarkDragOperationMetadata;
    const sourceData = ((_event$operation$sour = event.operation.source) === null || _event$operation$sour === void 0 ? void 0 : _event$operation$sour.data) as BookmarkDragOperationMetadata;
    if (event.operation.canceled || !sourceData || !targetData) {
      return;
    }
    mixpanel.track("[mp-query-builder] Drag operation", {
      sourceData,
      targetData
    });

    /** rearrange metrics */
    if (findKeyByValue(BookmarkTypes.TopLevelMetricType, sourceData.type) && findKeyByValue(BookmarkTypes.TopLevelMetricType, targetData.type)) {
      return handleMetricsRearrange({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning
      });
    }

    /** perform an event filter drop onto an event inside a metric */
    if (sourceData.type === "inline-filter" && targetData.type === "behavior-event") {
      return handleFilterAcrossMetricEvents({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning
      });
    }

    /** perform a global filter drop onto an event inside a metric */
    if (sourceData.type === "global-filter" && targetData.type === "behavior-event") {
      return handleGlobalFilerToMetricEvent({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning
      });
    }

    /** rearrange global filters section */
    if (sourceData.type === "global-filter" && targetData.type === "global-filter") {
      return handleGlobalFiltersRearrange({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning
      });
    }

    /** rearrange events inside a metric */
    if (sourceData.type === "behavior-event" && targetData.type === "behavior-event" && targetData.group === sourceData.group) {
      handleMetricEventsRearrange({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData
      });
    }

    /** rearrange filters inside a metric event */
    if (sourceData.type === "inline-filter" && targetData.type === "inline-filter" && ((_targetData$behaviorI = targetData.behaviorIndex) === null || _targetData$behaviorI === void 0 ? void 0 : _targetData$behaviorI.toString()) === ((_sourceData$behaviorI = sourceData.behaviorIndex) === null || _sourceData$behaviorI === void 0 ? void 0 : _sourceData$behaviorI.toString()) && targetData.group === sourceData.group) {
      handleMetricEventFiltersRearrange({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning
      });
    }
  }, [props.report]);
  return <DragDropProvider onBeforeDragStart={handleOnBeforeDrag} onDragEnd={onDragEnd} data-sentry-element="DragDropProvider" data-sentry-component="BookmarkDragDropProvider" data-sentry-source-file="provider.tsx">
      {props.children}
    </DragDropProvider>;
}